<template>
  <div class="relative w-full">
    <div :class="boxClasses">
      <mobile-select
        v-if="isMobile"
        :has-error="error"
        :icon-classes="iconClasses"
        :initial-selected="initialSelected"
        :options="options"
        :placeholder="placeholder"
        :select-classes="selectClasses"
        :text-classes="textClasses"
        :is-disabled="isDisabled"
        :has-localized-labels="hasLocalizedLabels"
        @select="onItemSelected"
      />
      <desktop-select
        v-else
        :has-error="error"
        :icon-classes="iconClasses"
        :initial-selected="initialSelected"
        :has-localized-labels="hasLocalizedLabels"
        :options="filteredOptions"
        :placeholder="placeholder"
        :select-classes="selectClasses"
        :text-classes="textClasses"
        :is-disabled="isDisabled"
        :is-relative="desktopRelative"
        @select="onItemSelected"
        @toggle="onToggleOptions"
        @select:hide-options="onClickHideOptions"
      />
    </div>
  </div>
</template>

<script>
import VueTypes from 'vue-types'

import MobileSelect from './mobileSelect'
import DesktopSelect from './desktopSelect'
import get from '~/utils/get'

export default {
  name: 'UiSelect',
  components: {
    MobileSelect,
    DesktopSelect,
  },
  props: {
    hasError: VueTypes.bool.def(false),
    placeholder: VueTypes.string.def(''),
    options: VueTypes.array.def([]),
    initialValue: VueTypes.oneOfType([String, Number]).def(-1),
    width: VueTypes.oneOfType([String, Number]),
    isDisabled: VueTypes.bool.def(false),
    hasLocalizedLabels: VueTypes.bool.def(false),
    desktopRelative: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      iconClasses: '',
      error: this.hasError,
      boxClasses: this.setBoxClasses(this.hasError),
      selectClasses: this.setSelectedClasses(this.hasError),
      textClasses: this.setTextClasses(this.hasError),
      filteredOptions: this.buildFilteredOptions(
        this.options,
        this.initialValue
      ),
    }
  },
  computed: {
    isMobile() {
      return this.$mq === 'sm'
    },
    initialSelected() {
      return get(this, `options[${this.initialValue - 1}]`, {})
    },
  },
  methods: {
    buildFilteredOptions(options, initialValue) {
      if (!initialValue) return options
      return options.filter((_, index) => index !== initialValue - 1)
    },
    onItemSelected(selected, { fromMobile }) {
      if (!fromMobile) this.toggle()

      this.filteredOptions = this.options.filter(
        op => op.value !== selected.value
      )

      this.$emit('select', selected)
    },
    toggle() {
      this.onToggleOptions()
      this.resetError()
    },
    onToggleOptions() {
      const isEmptyOrNegative =
        this.iconClasses === '' || this.iconClasses === '-rotate-180'
      this.iconClasses = isEmptyOrNegative ? 'rotate-180' : '-rotate-180'
    },
    onClickHideOptions() {
      this.iconClasses = '-rotate-180'
    },
    resetError() {
      this.error = false
      this.selectClasses = this.setSelectedClasses()
      this.boxClasses = this.setBoxClasses()
      this.textClasses = this.setTextClasses()
    },
    setSelectedClasses(hasInitialError) {
      const base =
        'bg-white w-full py-3 px-4 leading-tight rounded text-lg overflow-hidden appearance-none cursor-pointer'
      const textDefault = 'text-primary placeholder:text-primary'
      const textError = 'text-error placeholder:text-error'

      const text = this.error || hasInitialError ? textError : textDefault
      const bg = this.error || hasInitialError ? 'bg-error-light' : 'bg-white'

      return [base, text, bg]
    },
    setBoxClasses(hasInitialError) {
      const base = 'border max-w-xl rounded appearance-none mb-0 relative'
      const borderError = 'border-error'
      const width = `w-${this.width ? this.width : 'full'}`
      const borderDefault = 'border-gray hover:border-gray-dark'
      const border = this.error || hasInitialError ? borderError : borderDefault
      return [base, border, width]
    },
    setTextClasses(hasInitialError) {
      const text =
        this.error || hasInitialError ? 'text-error' : 'text-gray-black'
      return [text, 'leading-tight']
    },
  },
}
</script>
